import { useId, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, HelpCircle, LogOut, ShieldAlert } from 'lucide-react';
import type { EvacuationPoint, RollCallPerson, RollCallStatus } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { Avatar, Badge, Button, EmptyState } from '../ui';

export type RollCallTab = 'all' | 'unaccounted' | 'missing' | `point:${string}`;

const STATUS_ORDER: RollCallStatus[] = ['safe', 'missing', 'off_site', 'unaccounted'];

const STATUS_LABEL: Record<RollCallStatus, string> = {
  safe: 'p3w.roll.status.safe',
  missing: 'p3w.roll.status.missing',
  off_site: 'p3w.roll.status.offSite',
  unaccounted: 'p3w.roll.status.unaccounted',
};

/** People shown under a tab (a muster point tab shows people assigned to or marked at that point), narrowed by a name, group or host search. */
export function filterRollCall(people: readonly RollCallPerson[], tab: RollCallTab, search: string): RollCallPerson[] {
  const q = search.trim().toLowerCase();
  return people.filter((p) => {
    if (tab === 'unaccounted' && p.status !== 'unaccounted') return false;
    if (tab === 'missing' && p.status !== 'missing') return false;
    if (tab.startsWith('point:') && p.pointId !== tab.slice(6)) return false;
    if (!q) return true;
    return [p.name, p.groupName, p.hostName ?? ''].some((s) => s.toLowerCase().includes(q));
  });
}

/** Counts for the tabs and summary. */
export function rollCallCounts(people: readonly RollCallPerson[]): Record<RollCallStatus | 'total', number> {
  const c = { total: people.length, safe: 0, missing: 0, off_site: 0, unaccounted: 0 };
  for (const p of people) c[p.status] += 1;
  return c;
}

export interface RollCallListProps {
  people: readonly RollCallPerson[];
  points: readonly EvacuationPoint[];
  /** Shows the green "Live" dot when the live channel is connected. */
  live?: boolean;
  /** Hide the action buttons (read-only view, for example a report). */
  readOnly?: boolean;
  onMark?: (person: RollCallPerson, status: RollCallStatus) => void;
  onChoosePoint?: (person: RollCallPerson, pointId: string | null) => void;
}

/** The live roll call: tabs, counts, search, and a row per person with status buttons. */
export function RollCallList({ people, points, live, readOnly, onMark, onChoosePoint }: RollCallListProps) {
  const sid = useId();
  const [tab, setTab] = useState<RollCallTab>('unaccounted');
  const [search, setSearch] = useState('');
  const counts = useMemo(() => rollCallCounts(people), [people]);
  const rows = useMemo(() => filterRollCall(people, tab, search), [people, tab, search]);
  const pointName = (id: string | null) => points.find((p) => p.id === id)?.name ?? '';

  const tabs: Array<{ id: RollCallTab; label: string; count: number }> = [
    { id: 'all', label: t('p3w.roll.tab.all'), count: counts.total },
    { id: 'unaccounted', label: t('p3w.roll.tab.unaccounted'), count: counts.unaccounted },
    { id: 'missing', label: t('p3w.roll.tab.missing'), count: counts.missing },
    ...points
      .filter((p) => p.active)
      .map((p) => ({ id: `point:${p.id}` as RollCallTab, label: p.name, count: people.filter((x) => x.pointId === p.id).length })),
  ];

  return (
    <section aria-label={t('p3w.roll.title')} className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p role="status" className="text-sm text-slate-700">
          {t('p3w.roll.summary', { safe: counts.safe, total: counts.total, missing: counts.missing, offSite: counts.off_site })}
        </p>
        {live !== undefined ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700">
            <span aria-hidden="true" className={`h-2 w-2 rounded-full ${live ? 'bg-green-600' : 'bg-slate-400'}`} />
            {live ? t('p3w.roll.live') : t('p3w.roll.offline')}
          </span>
        ) : null}
      </div>

      <div role="tablist" aria-label={t('p3w.roll.tabs')} className="flex flex-wrap gap-1 border-b border-slate-200">
        {tabs.map((x) => (
          <button
            key={x.id}
            type="button"
            role="tab"
            aria-selected={tab === x.id}
            className={`-mb-px min-h-10 border-b-2 px-3 text-sm font-medium ${tab === x.id ? 'border-[var(--brand-primary)] text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
            onClick={() => setTab(x.id)}
          >
            {x.label} <span className="tabular-nums text-slate-600">({x.count})</span>
          </button>
        ))}
      </div>

      <div>
        <label htmlFor={`${sid}-search`} className="sr-only">
          {t('p3w.roll.search')}
        </label>
        <input
          id={`${sid}-search`}
          type="search"
          value={search}
          placeholder={t('p3w.roll.search')}
          onChange={(e) => setSearch(e.target.value)}
          className="min-h-10 w-full rounded-md border border-slate-300 px-3 text-sm sm:max-w-xs"
        />
      </div>

      {rows.length === 0 ? (
        <EmptyState message={t('p3w.roll.empty')} />
      ) : (
        <ul role="tabpanel" className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
          {rows.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-3 p-3">
              <Avatar name={p.name} />
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                  {p.name}
                  {p.isVisitor ? <Badge>{t('p3w.roll.visitor')}</Badge> : null}
                  {p.openPermits > 0 ? (
                    <Badge tone="warn">
                      <ShieldAlert size={12} aria-hidden="true" /> {t('p3w.roll.permits', { count: p.openPermits })}
                    </Badge>
                  ) : null}
                  {p.check ? (
                    <Badge tone="bad">
                      <AlertTriangle size={12} aria-hidden="true" /> {t('p3w.roll.check')}
                    </Badge>
                  ) : null}
                </p>
                <p className="text-xs text-slate-600">
                  {[p.groupName, p.hostName ? t('p3w.roll.host', { name: p.hostName }) : '', pointName(p.pointId)].filter(Boolean).join(' · ')}
                </p>
                {p.notes.length > 0 ? <p className="text-xs text-slate-600">{p.notes.map((n) => t(n.key, n.params)).join(' · ')}</p> : null}
              </div>
              <StatusMark status={p.status} />
              {!readOnly ? (
                <div className="flex flex-wrap items-center gap-1" role="group" aria-label={t('p3w.roll.actionsFor', { name: p.name })}>
                  {STATUS_ORDER.filter((s) => s !== 'unaccounted' && s !== p.status).map((s) => (
                    <Button key={s} onClick={() => onMark?.(p, s)}>
                      {t(`p3w.roll.mark.${s}`)}
                    </Button>
                  ))}
                  {onChoosePoint && points.length > 0 ? (
                    <select
                      aria-label={t('p3w.roll.choosePoint', { name: p.name })}
                      className="min-h-10 rounded-md border border-slate-300 px-2 text-sm sm:min-h-9"
                      value={p.pointId ?? ''}
                      onChange={(e) => onChoosePoint(p, e.target.value || null)}
                    >
                      <option value="">{t('p3w.roll.noPoint')}</option>
                      {points
                        .filter((x) => x.active)
                        .map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.name}
                          </option>
                        ))}
                    </select>
                  ) : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function StatusMark({ status }: { status: RollCallStatus }) {
  const tone = status === 'safe' ? 'good' : status === 'missing' ? 'bad' : status === 'off_site' ? 'neutral' : 'warn';
  const Icon = status === 'safe' ? CheckCircle2 : status === 'missing' ? AlertTriangle : status === 'off_site' ? LogOut : HelpCircle;
  return (
    <Badge tone={tone}>
      <Icon size={12} aria-hidden="true" /> {t(STATUS_LABEL[status])}
    </Badge>
  );
}
